import React, { useEffect, useState } from "react";
import {
    Button,
    Col,
    Row,
    Table,
    useTable,
    Select,
    Modal,
    useModalForm,
    Form,
    Input,
} from '@pankod/refine-antd';
import { InputValidatorRegex } from "interfaces/company";


enum TaxConfigurationType {
    IGST = 'IGST',
    CGST_AND_SGST = 'CGST_AND_SGST',
    TDS = 'TDS',
}

interface TaxConfigurationRecord {
    id: string;
    displayName: string;
    label: string;
    value: number;
    section: string;
    taxType: TaxConfigurationType;
    isArchived: boolean;
    reason: string,
}

type ResourceAction = 'create' | 'edit' | 'clone';
const DEFAULT_PAGE_LIMIT = 10;


export const TaxConfigurationList: React.FC = () => {
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: DEFAULT_PAGE_LIMIT,
    });

    const [tableRefetchKey, setTableRefetchKey] = useState(0);
    let [taxType, setTaxType] = useState(TaxConfigurationType.TDS);
    const [editingRecord, setEditingRecord] = useState<TaxConfigurationRecord | null>(null);
    const [taxConfigModalConfig, setTaxConfigModalConfig] = useState({
        resource: 'tax-percentage-config/internal-portal',
        action: 'create' as ResourceAction,
        title: 'Create Tax Config',
        okText: 'Create',
        confirmLoading: false,
    });
    const [archiveTaxConfigModalConfig, setArchiveTaxConfigModalConfig] = useState({
        resource: `tax-percentage-config/internal-portal/toggle-archive/id`,
        action: 'create' as ResourceAction,
        title: "Archive Tax Configuration",
        okText: "Archive",
        confirmLoading: false,
    });
    const [archivingRecord, setArchivingRecord] = useState<TaxConfigurationRecord | null>(null);

    // Datatable
    const { tableProps } = useTable({
        dataProviderName: "grapghQl",
        resource: 'taxPercentageConfigList',
        syncWithLocation: false, // TODO: revisit
        metaData: {
            type: "query",
            where: {
                pagination: {
                    limit: pagination.pageSize,
                    page: pagination.current,
                }
            },
            filters: {
                taxType,
                filterOnlyActive: false,
                filterOnlyArchived: false,
            },
            tableRefetchKey, // To trigger refetch
        },
    });

    const handleTaxTypeChange = (value: TaxConfigurationType) => {
        setTaxType(value);
    };

    const handleTablePageChange = (page: number, pageSize: number) => {
        setPagination({
            current: page,
            pageSize: DEFAULT_PAGE_LIMIT,
        });
    };

    // Add / Edit Tax Config Modal
    const {
        modalProps: taxConfigModal,
        formProps: taxConfigForm,
        show: showTaxConfigModal,
        close: closeTaxConfigModal,
    } = useModalForm<any>({
        resource: taxConfigModalConfig.resource,
        action: taxConfigModalConfig.action,
        metaData: {
        },
        redirect: false,
        onMutationSuccess() {
            setTableRefetchKey(prevKey => prevKey + 1);
            setEditingRecord(null);
        },
    });
    taxConfigModal.title = taxConfigModalConfig.title;
    taxConfigModal.confirmLoading = true;
    taxConfigModal.centered = true;
    taxConfigModal.okText = taxConfigModalConfig.okText;
    taxConfigModal.width = 500;
    taxConfigModal.onCancel = () => {
        taxConfigForm.form?.resetFields();
        setEditingRecord(null);
        closeTaxConfigModal();
    }

    const showAddNewTaxConfigModal = () => {
        taxConfigForm?.form?.resetFields();
        setTaxConfigModalConfig({
            resource: `tax-percentage-config/internal-portal`,
            action: 'create' as ResourceAction,
            title: 'Create Tax Config',
            okText: 'Create',
            confirmLoading: false,
        });
        setEditingRecord(null);
        showTaxConfigModal();
    };

    const showEditTaxConfigModal = (record: TaxConfigurationRecord) => {
        setEditingRecord(null);
        setTaxConfigModalConfig({
            resource: `tax-percentage-config/internal-portal/${record.id}`,
            action: 'edit' as ResourceAction,
            title: 'Edit Tax Config',
            okText: 'Update',
            confirmLoading: false,
        });
        setEditingRecord(record);
        showTaxConfigModal();
    };

    useEffect(() => {
        if (editingRecord) {
            taxConfigForm.form?.setFieldsValue({
                taxType: editingRecord.taxType,
                label: editingRecord.label,
                value: editingRecord.value,
                section: editingRecord.section,
                reason: null,
            });
        }
    }, [editingRecord, taxConfigForm.form]);


    // Archive Tax config
    const {
        modalProps: archiveTaxConfigModalProps,
        formProps: archiveTaxConfigFormProps,
        show: showArchiveTaxConfigModalForm,
        close: closeArchiveTaxConfigModalForm,
    } = useModalForm({
        resource: archiveTaxConfigModalConfig.resource,
        action: archiveTaxConfigModalConfig.action,
        redirect: false,
        metaData: {
            isArchived: true,
        },
        onMutationSuccess() {
            setTableRefetchKey(prevKey => prevKey + 1);
            setArchivingRecord(null);
        },
    });
    archiveTaxConfigModalProps.title = archiveTaxConfigModalConfig.title;
    archiveTaxConfigModalProps.centered = true;
    archiveTaxConfigModalProps.width = 500;
    archiveTaxConfigModalProps.onCancel = () => {
        archiveTaxConfigFormProps.form?.resetFields();
        setArchivingRecord(null);
        closeArchiveTaxConfigModalForm();
    };

    const showArchiveTaxConfigModal = (record: TaxConfigurationRecord) => {
        setArchivingRecord(record);
        setArchiveTaxConfigModalConfig({
            resource: `tax-percentage-config/internal-portal/toggle-archive/${record.id}`,
            action: 'create' as ResourceAction,
            title: "Archive Tax Configuration",
            okText: "Archive",
            confirmLoading: false,
        });
        showArchiveTaxConfigModalForm();
    };

    useEffect(() => {
        if (archivingRecord) {
            archiveTaxConfigFormProps.form?.setFieldsValue({
                reason: '',
            });
        }
    }, [archivingRecord, archiveTaxConfigFormProps.form]);


    return (
        <>
            <Row gutter={[16, 16]} className="mb-2">
                <Col lg={24} xs={24} style={{ textAlign: 'right' }}>
                    <Select placeholder="Select Tax Type" value={taxType} onChange={handleTaxTypeChange} style={{ width: 200 }}>
                        <Select.Option value={TaxConfigurationType.IGST}>IGST</Select.Option>
                        <Select.Option value={TaxConfigurationType.CGST_AND_SGST}>CGST/SGST</Select.Option>
                        <Select.Option value={TaxConfigurationType.TDS}>TDS</Select.Option>
                    </Select>
                    <Button onClick={showAddNewTaxConfigModal} className="ml-2 mr-2 mt-2">
                        Add New
                    </Button>
                </Col>
            </Row>
            <Row gutter={[16, 16]}>
                <Col lg={24} xs={24}>
                    <Table {...tableProps}
                        pagination={{
                            ...tableProps.pagination,
                            onChange: handleTablePageChange,
                            pageSize: DEFAULT_PAGE_LIMIT,
                        }}
                        rowKey="id"
                        size="small"
                        rowClassName={(record) => record.isArchived ? 'archived-row' : ''}
                    >
                        <Table.Column dataIndex="displayName" title="Display Name" />
                        <Table.Column dataIndex="label" title="Label" />
                        <Table.Column dataIndex="value" title="Percentage" />
                        <Table.Column dataIndex="section" title="Section" />
                        <Table.Column
                            dataIndex="createdAt"
                            title="Created At"
                            render={(value) => new Date(value).toLocaleDateString()}
                        />
                        <Table.Column
                            title="Actions"
                            render={(_, record: TaxConfigurationRecord) => (
                                <>
                                    {
                                        !record.isArchived && (
                                            <>
                                                <Button type="link" onClick={() => showEditTaxConfigModal(record)}>Edit</Button>
                                                <Button type="link" danger onClick={() => showArchiveTaxConfigModal(record)}>Archive</Button>
                                            </>
                                        )
                                    }
                                </>
                            )}
                        />
                    </Table>
                </Col>
            </Row>


            <Modal {...taxConfigModal}>
                <Form
                    {...taxConfigForm}
                    layout="vertical"
                    initialValues={{
                        taxType: '',
                        label: '',
                        value: null,
                        reason: null,
                    }}
                >

                    <Row gutter={16}>
                        <Col span={24}>
                            <Form.Item
                                name={["taxType"]}
                                label="Tax Type"
                                rules={[
                                    {
                                        required: true,
                                        message: "Please select the Tax Type",
                                    },
                                ]}
                            >
                                <Select placeholder="Select Tax Type" value={taxType} style={{ width: 200 }}>
                                    <Select.Option value={TaxConfigurationType.IGST}>IGST</Select.Option>
                                    <Select.Option value={TaxConfigurationType.CGST_AND_SGST}>CGST/SGST</Select.Option>
                                    <Select.Option value={TaxConfigurationType.TDS}>TDS</Select.Option>
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                name={["label"]}
                                label="Label"
                                rules={[{
                                    message: 'Label is Required',
                                    required: true
                                }]}
                            >
                                <Input />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                name={["value"]}
                                label="Percentage"
                                rules={[
                                    {
                                        message: 'Percentage is Required',
                                        required: true
                                    },
                                    {
                                        message: 'Incorrect value',
                                        pattern: InputValidatorRegex.PERCENTAGE,
                                    },
                                ]}>
                                <Input type="number" step="0.01" />
                            </Form.Item>
                        </Col>
                        <Col span={24}>
                            <Form.Item
                                name={["section"]}
                                label="Section"
                            >
                                <Input placeholder="Section (Optional)" />
                            </Form.Item>
                        </Col>
                        {
                            editingRecord && (
                                <Col span={24}>
                                    <Form.Item
                                        name={["reason"]}
                                        label="Reason"
                                        rules={[
                                            {
                                                message: 'Reason is Required',
                                                required: true
                                            },
                                        ]}>
                                        <Input.TextArea rows={3} placeholder="Enter reason" />
                                    </Form.Item>
                                </Col>
                            )
                        }
                    </Row>

                </Form>
            </Modal>

            <Modal {...archiveTaxConfigModalProps}>
                <Form {...archiveTaxConfigFormProps} layout="vertical">
                    <p>Are you sure you want to archive the tax configuration "{archivingRecord?.displayName}"?</p>
                    <Form.Item
                        name="reason"
                        label="Reason for Archiving"
                        rules={[{ required: true, message: "Please provide a reason for archiving." }]}
                    >
                        <Input.TextArea rows={3} placeholder="Enter reason" />
                    </Form.Item>
                </Form>
            </Modal>

        </>

    );
}